// Events data file - can be easily updated with new events
export interface Speaker {
  id: string;
  name: string;
  title: string;
  bio: string;
  image: string;
  linkedinUrl: string;
  role: string;
}

export interface EventSection {
  id: string;
  heading: string;
  content: string;
}

export interface Event {
  id: string;
  title: string;
  subtitle?: string;
  image: string;
  date: string;
  time: string;
  timezone?: string;
  category: 'webinar' | 'event' | 'workshop';
  seriesName?: string;
  ctaText: string;
  ctaLink: string;
  isLive?: boolean;
  featured?: boolean;
  tags?: string[];
  keywords?: string[];
  specialType?: 'off-the-record' | 'candid-talks';
  // New fields for single event page
  hubspotFormId?: string;
  lumaEventId?: string;
  tallyFormEmbed?: string;
  sections: EventSection[];
  speakers: Speaker[];
  longDescription?: string;
}

export const events: Event[] = [
  {
    id: 'job-portals-modern-sourcing-2026',
    title: 'From job portals to modern sourcing platforms — how hiring is changing in 2026',
    subtitle: '4 shifts that are reshaping job portals in 2026',
    longDescription: 'Resumes are abundant. What\'s scarce is relevance and speed. In this session, we\'ll break down 4 shifts job portals are facing and how are they reinventing themselves.',
    image: '/event-resdex-pricing.png',
    date: '2026-04-09',
    time: '16:00',
    timezone: 'IST',
    category: 'webinar',
    ctaText: 'Register Now',
    ctaLink: '/events/job-portals-modern-sourcing-2026',
    isLive: false,
    featured: true,
    tags: ['Job portals', 'Sourcing', 'Recruitment', 'Hiring'],
    keywords: ['job portals 2026', 'modern sourcing platforms', 'hiring trends 2026', 'talent acquisition', 'sourcing workflows', 'recruitment efficiency', 'candidate relevance', 'hiring speed', 'job portal evolution', 'sourcing tools'],
    hubspotFormId: 'ed023aa7-1c61-4941-bd83-9503d594b536',
    sections: [
      {
        id: 'overview',
        heading: 'What we will cover',
        content: 'Hiring teams today don\'t struggle with access to candidates — they struggle with finding the right ones quickly.\n\nTraditional job portals were built around resume volume and database access. But as hiring needs evolve, teams are looking for more relevance, speed, and efficiency from their sourcing workflows.\n\nIn this session, we\'ll break down the 4 key shifts that are challenging job portals in 2026 — and how the best teams are adapting to these changes.\n\nMore importantly, we\'ll show how you can move beyond using job portals just for applications — and start using them more effectively as sourcing tools for faster, higher-quality hiring.'
      },
      {
        id: 'takeaways',
        heading: '🎯 What you\'ll walk away with',
        content: '• A clear understanding of what\'s changing in sourcing today\n• How to get more relevant candidates instead of more resumes\n• Ways to reduce manual search and improve hiring speed\n• How to use job portals beyond their conventional use to drive better outcomes'
      },
      {
        id: 'audience',
        heading: '👥 Who this is for',
        content: '• Talent Acquisition leaders and recruiters\n• Hiring managers actively sourcing candidates\n• Teams using job portals and looking to improve results'
      }
    ],
    speakers: [
      {
        id: 'nikunj-verma',
        name: 'Nikunj Verma',
        title: 'CEO & Founder, Cutshort',
        bio: 'As a founder of Cutshort, Nikunj has seen the tech recruitment space evolving in India.',
        image: 'https://media.licdn.com/dms/image/v2/D4D03AQFM5jxDdoilUg/profile-displayphoto-shrink_400_400/B4DZaf2rcrGsAg-/0/1746438633706?e=1759968000&v=beta&t=rRhIMAF1y6CImc8Wq9yucCYzfXjnvByybVeULkzoWrw',
        linkedinUrl: 'https://linkedin.com/in/vermanikunj',
        role: 'Speaker'
      }
    ]
  },
  {
    id: 'inside-software-job-market-recruiters-speak-off-record',
    title: 'Inside the Software Engineering Job Market — When Recruiters Speak Off the Record',
    subtitle: 'Forget the headlines. Real recruiters go incognito to reveal what\'s actually happening in tech hiring. Candid. Real. Off the record.',
    longDescription: 'Join a panel of experienced recruiters and TA leaders — all joining incognito — share what they\'ve actually seen over the past year.',
    image: '/inside-job-market.png',
    date: '2025-10-29',
    time: '18:00',
    timezone: 'IST',
    category: 'event',
    seriesName: 'Cutshort Candid Talks',
    ctaText: 'View Event',
    ctaLink: '/events/inside-software-job-market-recruiters-speak-off-record',
    isLive: false,
    featured: true,
    tags: ['Off the Record', 'Recruitment', 'Tech Hiring', 'Candid Talks'],
    keywords: ['off the record', 'recruiter insights', 'tech hiring trends', 'software engineering jobs', 'recruiter panel', 'hiring market', 'tech salaries', 'AI jobs', 'remote work', 'counteroffers', 'tech skills', 'hiring trends 2025'],
    specialType: 'off-the-record',
    tallyFormEmbed: "https://tally.so/embed/3j1N89?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1",
    sections: [
      {
        id: 'overview',
        heading: 'What we will cover',
        content: 'In this Candid Talks: Off the Record session, a panel of experienced recruiters and TA leaders — all joining incognito — share what they\'ve actually seen over the past year:\n\n• Is tech hiring really slowing down, or just shifting?\n• How have salaries, roles, and expectations evolved since 2022?\n• Is AI replacing jobs or reshaping them?\n• Which skills are quietly rising, and which are losing relevance?\n• How are counteroffers and "remote" roles playing out in 2025?\n\nNo spin. No reports. Just honest voices from the hiring trenches.'
      }
    ],
    speakers: [
      {
        id: 'guest-e',
        name: 'Guest E',
        title: 'Seasoned recruiter consultant for 100+ tech companies',
        bio: 'Has helped 100+ startups, IT consulting and GCCs in India in hiring engineering talent',
        image: '/speaker-placeholder.svg',
        linkedinUrl: '#',
        role: 'Speaker'
      },
      {
        id: 'guest-b',
        name: 'Guest B',
        title: 'Senior Manager at a leading IT solutions provider',
        bio: 'Brings rich experience in large-scale tech hiring for digital transformation projects across multiple industries.',
        image: '/speaker-placeholder.svg',
        linkedinUrl: '#',
        role: 'Speaker'
      },
      {
        id: 'guest-m',
        name: 'Guest M',
        title: 'Senior Talent Acquisition Director bringing GCCs to India',
        bio: 'Has a front-row view of how global hiring strategies are evolving as India becomes the hub for engineering excellence.',
        image: '/speaker-placeholder.svg',
        linkedinUrl: '#',
        role: 'Speaker'
      },
      {
        id: 'guest-s',
        name: 'Guest S',
        title: 'People and Culture Leader at a Tier-1 SaaS startup in Bengaluru',
        bio: 'Having seen startups closely, offers insights on balancing growth, culture, and retention in a dynamic hiring landscape.',
        image: '/speaker-placeholder.svg',
        linkedinUrl: '#',
        role: 'Speaker'
      }
    ]
  },
  // {
  //   id: 'recruiters-shortlist-applications',
  //   title: 'How recruiters shortlist applications in real life?',
  //   subtitle: 'Understand directly from the experienced recruiters. Some of them will be anonymous!',
  //   longDescription: 'Wondering why you never hear back from recruiters after applying? Understand the real reasons and fix things to land your dream job.',
  //   image: '/event-placeholder.svg',
  //   date: '2025-10-17',
  //   time: '17:00',
  //   timezone: 'IST',
  //   category: 'event',
  //   seriesName: 'Cutshort Candid Talks',
  //   ctaText: 'View Event',
  //   ctaLink: '/events/recruiters-shortlist-applications',
  //   isLive: false,
  //   featured: true,
  //   tags: ['Recruitment', 'Career Tips', 'Job Search'],
  //   keywords: ['recruiter shortlisting', 'job application tips', 'why recruiters don\'t respond', 'how to get shortlisted', 'recruiter insights', 'job search strategies', 'application rejection', 'recruiter decision making', 'career advice', 'job hunting tips'],
  //   tallyFormEmbed: "https://tally.so/embed/3j1N89?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1",
  //   sections: [
  //     {
  //       id: 'overview',
  //       heading: 'What we will cover',
  //       content: 'This event will feature experienced recruiters who have made 1000+ offers across startups, product and consulting companies.\nSome of the recruiters we will invite will join as anonymous for a really candid discussion.\n\n1. Understanding the environment recruiting teams work in - their goals and systems\n2. How do they go about and what they look for?\n3. What makes them shortlist? What makes them skip? Why don\'t they reject?\n4. Interesting anecdotes and candid discussion'
  //     }
  //   ],
  //   speakers: [
  //     {
  //       id: 'nikunj-verma-candid',
  //       name: 'Nikunj Verma',
  //       title: 'CEO & Founder, Cutshort',
  //       bio: 'As a founder of Cutshort, Nikunj has seen the tech recruitment space evolving in India.',
  //       image: 'https://media.licdn.com/dms/image/v2/D4D03AQFM5jxDdoilUg/profile-displayphoto-shrink_400_400/B4DZaf2rcrGsAg-/0/1746438633706?e=1759968000&v=beta&t=rRhIMAF1y6CImc8Wq9yucCYzfXjnvByybVeULkzoWrw',
  //       linkedinUrl: 'https://linkedin.com/in/vermanikunj',
  //       role: 'Host'
  //     }
  //   ]
  // },
  {
    id: 'job-portals-pricing',
    title: 'The end of high Resdex prices and the 8.33% agency model?',
    longDescription: 'Resume databases renewals get 10% to 35% expensive every year. Agencies? Their pricing has remained steady at 8.33% but they remain prohibitively expensive. But with AI, it looks both these models are going to get disrupted soon. Join to explore how!',
    image: '/event-resdex-pricing.png',
    date: '2025-09-18',
    time: '16:00',
    timezone: 'EST',
    category: 'webinar',
    ctaText: 'View Event',
    ctaLink: '/events/job-portals-pricing',
    isLive: false,
    featured: true,
    tags: ['Job portals', 'Recruitment', 'Pricing'],
    keywords: ['job portals pricing', 'resdex pricing', 'recruitment agency model', 'AI recruitment disruption', 'resume database costs', 'hiring costs', 'recruitment pricing trends', 'talent acquisition costs', 'recruitment technology', 'hiring automation'],
    hubspotFormId: '86392371-2a3c-49c3-a04e-176427813613',
    sections: [
      {
        id: 'overview',
        heading: 'What we will cover',
        content: 'We will look at the \n\n1. The impact of AI in recruitment and where it\'s successful and where it\'s not. \n2. The evolution of job portals offerings and pricing models. \n3. The evolution and economics of recruitment agency models\n4. The AI impact on both of these offerings and pricing models\n5. Connecting the dots to see where the pricing models will go from here\n6. Open & candid discussion'
      }
    ],
    speakers: [
      {
        id: 'nikunj-verma',
        name: 'Nikunj Verma',
        title: 'CEO & Founder, Cutshort',
        bio: 'As a founder of Cutshort, Nikunj has seen the tech recruitment space evolving in India.',
        image: 'https://media.licdn.com/dms/image/v2/D4D03AQFM5jxDdoilUg/profile-displayphoto-shrink_400_400/B4DZaf2rcrGsAg-/0/1746438633706?e=1759968000&v=beta&t=rRhIMAF1y6CImc8Wq9yucCYzfXjnvByybVeULkzoWrw',
        linkedinUrl: 'https://linkedin.com/in/vermanikunj',
        role: 'Speaker'
      },
      {
        id: 'ranaq-sen',
        name: 'Ranaq Sen',
        title: 'Founder, PeopleBridge Labs',
        bio: 'Ranaq has headed people function at WorkIndia and now runs PeopleBridge Labs, a new-age HR consulting firm. He also hosts the popular "The CuHRious Show" podcast.',
        image: 'https://media.licdn.com/dms/image/v2/D5603AQFy5vj9gKtuMQ/profile-displayphoto-shrink_400_400/B56ZaCNLhTHgAk-/0/1745941216533?e=1761177600&v=beta&t=-elM3uVOra28QYVu7-DnNFafpZPjvuTXhcTxpy5ahcU',
        linkedinUrl: 'https://www.linkedin.com/in/ranaq/',
        role: 'QnA Host'
      }
    ]
  }
];

// Helper functions for filtering and sorting events
export const getFeaturedEvents = () => events.filter(event => event.featured);

export const getEventsByCategory = (category: Event['category']) => 
  events.filter(event => event.category === category);

export const getUpcomingEvents = () => {
  const now = new Date();
  return events.filter(event => {
    const eventDate = new Date(`${event.date}T${event.time}`);
    return eventDate > now;
  }).sort((a, b) => {
    const dateA = new Date(`${a.date}T${a.time}`);
    const dateB = new Date(`${b.date}T${b.time}`);
    return dateA.getTime() - dateB.getTime();
  });
};

export const getEventById = (id: string) => events.find(event => event.id === id);


